import {DependencyList, useCallback, useEffect, useRef} from 'react';

type DeferredAction<T extends DependencyList> = {
	action: (...dependencies: T) => void | Promise<void>;
	resolve: () => void;
	reject: (error: Error) => void;
};

/**
 * useDeferredAction custom hook: runs an action right away when `condition` holds, otherwise once the
 * dependencies make it hold. Actions deferred meanwhile are queued and run in order, each after the
 * previous one settled: every returned promise settles (a single slot left the overwritten ones pending).
 */
export default function useDeferredAction<T extends DependencyList>(
	dependencies: T,
	condition: (...deps: T) => boolean
) {
	const deferredActionsRef = useRef<DeferredAction<T>[]>([]);

	const deferAction = useCallback((action: (...dependencies: T) => void | Promise<void>) => {
		if (condition(...dependencies)) {
			return action(...dependencies);
		} else {
			return new Promise<void>((resolve, reject) => {
				deferredActionsRef.current.push({action, resolve, reject});
			});
		}
	}, [condition, ...dependencies]);

	useEffect(() => {
		if (condition(...dependencies) && deferredActionsRef.current.length) {
			const actions = deferredActionsRef.current;

			deferredActionsRef.current = [];
			actions.reduce(
				(previous, {action, resolve, reject}) =>
					previous
						.then(() => action(...dependencies))
						.then(resolve, reject),
				Promise.resolve(),
			);
		}
	}, [...dependencies]);

	useEffect(() => {
		return () => {
			deferredActionsRef.current = [];
		};
	}, []);

	return {deferAction};
};
